// src/pages/Wallet/modals/WalletSelectorModal.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import { WalletContext } from '../../../contexts/WalletContext';
import { useTranslation } from 'react-i18next';
import './WalletSelectorModal.css';
// Import secure memory utilities
import {
  createSecureReference,
  getSecureData,
  secureClearData,
  createTemporaryAccess
} from '../../../utils/secureMemoryUtils';

// SVG icons as components to replace react-icons - IMPROVED FOR MOBILE
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-svg">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-svg">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const CopyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-svg">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-svg">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const DragHandleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-svg">
    <line x1="8" y1="6" x2="16" y2="6"></line>
    <line x1="8" y1="12" x2="16" y2="12"></line>
    <line x1="8" y1="18" x2="16" y2="18"></line>
  </svg>
);

const WalletSelectorModal = ({
  isOpen,
  onClose,
  wallets,
  currentWallet,
  onSwitchWallet,
  onCreateWallet,
  onImportWallet,
  onRemoveWallet,
  onUpdateWalletName,
  onReorderWallets, // New prop for reordering wallets
}) => {
  const { t } = useTranslation();
  
  // Get auth context for password verification
  const { isAuthenticated, verifyPassword, hasPassword, passwordHash, passwordSalt } = useContext(AuthContext);
  // Get wallet context for seed phrase access
  const { getSeedPhrase, isWalletFromSeedPhrase, hasSeedPhrase } = useContext(WalletContext);

  const [isAdding, setIsAdding] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // SECURITY IMPROVEMENT: Use secure reference for import PK instead of direct state
  const [importPkToken, setImportPkToken] = useState(null);
  // For UI purposes only - doesn't store actual data
  const [hasEnteredImportData, setHasEnteredImportData] = useState(false);
  
  // State for drag and drop
  const [orderedWallets, setOrderedWallets] = useState([]);
  const [draggedWallet, setDraggedWallet] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  const [isProcessing, setIsProcessing] = useState(false);

  // States for viewing private key or seed phrase
  const [isViewingKey, setIsViewingKey] = useState(false);
  const [isViewingSeed, setIsViewingSeed] = useState(false);
  const [selectedWalletAddress, setSelectedWalletAddress] = useState(null);
  // SECURITY IMPROVEMENT: Use secure reference for selected wallet
  const [selectedWalletToken, setSelectedWalletToken] = useState(null);
  
  const [privateKeyPassword, setPrivateKeyPassword] = useState('');
  const [privateKeyError, setPrivateKeyError] = useState('');
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showSeedPhrase, setShowSeedPhrase] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSeed, setCopiedSeed] = useState(false);
  const [importError, setImportError] = useState('');

  // Ensure we're showing the correct wallet list after reordering
  useEffect(() => {
    if (isOpen && wallets?.length) {
      // Keep orderedWallets in sync with the source wallets
      // This handles cases where parent might update the wallet list
      setOrderedWallets([...wallets]);
    }
  }, [isOpen, wallets]);

  // Secure function to store private key or sensitive data
  const secureStoreData = (data, timeoutMs = 2 * 60 * 1000) => {
    if (!data) return null;
    return createSecureReference(data, timeoutMs);
  };

  // Cleanup function to securely clear sensitive data
  const clearSensitiveData = () => {
    // Clear import PK
    if (importPkToken) {
      secureClearData(importPkToken);
      setImportPkToken(null);
      setHasEnteredImportData(false);
    }
    
    // Clear selected wallet token
    if (selectedWalletToken) {
      secureClearData(selectedWalletToken);
      setSelectedWalletToken(null);
    }
    
    // Clear password (securely overwrite before clearing)
    if (privateKeyPassword) {
      const length = privateKeyPassword.length;
      setPrivateKeyPassword('0'.repeat(length));
      setPrivateKeyPassword('');
    }
  };

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Securely clear sensitive data first
      clearSensitiveData();
      
      // Reset all states when modal closes
      setIsAdding(false);
      setIsImporting(false);
      setIsEditing(false);
      setIsProcessing(false);
      setIsViewingKey(false);
      setIsViewingSeed(false);
      setSelectedWalletAddress(null);
      setPrivateKeyError('');
      setShowPrivateKey(false);
      setShowSeedPhrase(false);
      setCopiedKey(false);
      setCopiedSeed(false);
      setImportError('');
      setDraggedWallet(null);
      setDraggedIndex(null);
      setDragOverIndex(null);
    }
  }, [isOpen]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearSensitiveData();
    };
  }, []);

  if (!isOpen) return null;

  // Secure setter for import PK
  const handleImportPKChange = (value) => {
    // Clear previous token
    if (importPkToken) {
      secureClearData(importPkToken);
    }
    
    if (value) {
      // Create new secure reference
      const token = secureStoreData(value);
      setImportPkToken(token);
      setHasEnteredImportData(true);
    } else {
      setImportPkToken(null);
      setHasEnteredImportData(false);
    }
  };

  // Handler for creating a new wallet
  const handleCreateWallet = async () => {
    try {
      setIsProcessing(true);
      
      // Create new wallet
      const newWallet = await onCreateWallet();
      
      // Make sure the wallet was created successfully
      if (!newWallet || !newWallet.address) {
        throw new Error("Failed to create wallet - missing wallet data");
      }
      
      setIsAdding(false);
      setIsImporting(false);
    } catch (err) {
      console.error("Error creating wallet:", err);
      alert('Failed to create wallet: ' + (err.message || 'Unknown error'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle importing a wallet
  const handleImportWalletClick = async () => {
    if (!importPkToken) {
      setImportError(t('wallet.selectorModal.errors.enterPrivateKey'));
      return;
    }
    
    try {
      setIsProcessing(true);
      setImportError('');
      
      // Securely access the import data
      const importData = getSecureData(importPkToken);
      if (!importData) {
        throw new Error(t('wallet.selectorModal.errors.importDataExpired'));
      }
      
      await onImportWallet(importData);
      
      // Clear the import data securely
      secureClearData(importPkToken);
      setImportPkToken(null);
      setHasEnteredImportData(false);
      
      setIsAdding(false);
      setIsImporting(false);
    } catch (err) {
      setImportError(err.message || t('wallet.selectorModal.errors.importFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler for wallet name changes with auto-save
  const handleNameChange = (walletObj, newName) => {
    // If name is empty, don't update
    if (newName.trim() === '') return;
    
    // Auto-save the new name
    onUpdateWalletName(walletObj, newName);
  };

  // Handler for wallet removal with confirmation
  const handleRemoveWalletConfirm = (w) => {
    const sure = window.confirm(t('wallet.selectorModal.deleteConfirmation', { name: w.name || t('wallet.selectorModal.unnamed') }));
    if (sure) {
      onRemoveWallet(w);
    }
  };

  // Handler for viewing private key
  const handleViewPrivateKey = (wallet) => {
    // Clear previous tokens first
    clearSensitiveData();
    
    // Store wallet address for easier access and UI reference
    setSelectedWalletAddress(wallet.address);
    
    // Securely store the wallet object
    const token = secureStoreData(wallet);
    setSelectedWalletToken(token);
    
    setIsViewingKey(true);
    setIsViewingSeed(false);
    setPrivateKeyPassword('');
    setPrivateKeyError('');
    setCopiedKey(false);
    
    // If user doesn't have a password set, show the private key immediately
    if (!hasPassword) {
      setShowPrivateKey(true);
    } else {
      setShowPrivateKey(false);
    }
  };

  // Handler for viewing seed phrase
  const handleViewSeedPhrase = () => {
    setIsViewingSeed(true);
    setIsViewingKey(false);
    setPrivateKeyPassword('');
    setPrivateKeyError('');
    setCopiedSeed(false);
    setShowSeedPhrase(false);
    
    // If user doesn't have a password set, show the seed phrase immediately
    if (!hasPassword) {
      setShowSeedPhrase(true);
    }
  };

  // Handler for verifying password and showing private key
  const handleVerifyPasswordForKey = async () => {
    if (!privateKeyPassword) {
      setPrivateKeyError(t('wallet.selectorModal.errors.enterPassword'));
      return;
    }

    try {
      setIsProcessing(true);
      
      // If hasPassword is true, verify the password
      if (hasPassword && passwordHash && passwordSalt) {
        const isValid = await verifyPassword(privateKeyPassword, passwordHash, passwordSalt);
        if (!isValid) {
          setPrivateKeyError(t('wallet.selectorModal.errors.incorrectPassword'));
          setIsProcessing(false);
          return;
        }
      }
      
      // If we get here, password is verified or not needed
      setPrivateKeyError('');
      
      if (isViewingKey) {
        setShowPrivateKey(true);
      } else if (isViewingSeed) {
        setShowSeedPhrase(true);
      }
      
      // Clear password field securely now that it's used
      const length = privateKeyPassword.length;
      setPrivateKeyPassword('0'.repeat(length));
      setPrivateKeyPassword('');
      
      setIsProcessing(false);
    } catch (error) {
      console.error('Error verifying password:', error);
      setPrivateKeyError(t('wallet.selectorModal.errors.verificationError'));
      setIsProcessing(false);
    }
  };

  // Handler for copying private key
  const handleCopyPrivateKey = () => {
    if (!selectedWalletToken) return;
    
    try {
      // Securely access the wallet data
      const wallet = getSecureData(selectedWalletToken, true);
      
      if (wallet && wallet.privateKey) {
        navigator.clipboard.writeText(wallet.privateKey);
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 3000);
      }
    } catch (error) {
      console.error('Error copying private key:', error);
    }
  };

  // Handler for copying seed phrase
  const handleCopySeedPhrase = () => {
    const seedPhrase = getSeedPhrase();
    if (seedPhrase) {
      navigator.clipboard.writeText(seedPhrase);
      setCopiedSeed(true);
      setTimeout(() => setCopiedSeed(false), 3000);
    }
  };
  
  // Handler for cancelling add mode
  const handleCancelAdd = () => {
    // Clear import data securely
    if (importPkToken) {
      secureClearData(importPkToken);
      setImportPkToken(null);
      setHasEnteredImportData(false);
    }
    
    setIsAdding(false);
    setIsImporting(false);
  };

  // Handler for exiting edit mode - save reordering changes
  const handleCancelEdit = () => {
    setIsEditing(false);
    
    // Save any reordered wallets if the order has changed
    if (orderedWallets.length > 0 && 
        JSON.stringify(orderedWallets.map(w => w?.address)) !== JSON.stringify(wallets.map(w => w?.address))) {
      // Call the parent's reorder function to persist changes
      if (onReorderWallets) {
        onReorderWallets(orderedWallets);
      }
    }
    
    // Clear drag state
    setDraggedWallet(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Handler for cancelling view private key mode
  const handleCancelViewKey = () => {
    setIsViewingKey(false);
    
    // Clear wallet data securely
    if (selectedWalletToken) {
      secureClearData(selectedWalletToken);
      setSelectedWalletToken(null);
    }
    
    setSelectedWalletAddress(null);
    setPrivateKeyPassword('');
    setPrivateKeyError('');
    setShowPrivateKey(false);
    // Keep edit mode active when returning
    setIsEditing(true);
  };

  // Handler for cancelling view seed phrase mode
  const handleCancelViewSeed = () => {
    setIsViewingSeed(false);
    setPrivateKeyPassword('');
    setPrivateKeyError('');
    setShowSeedPhrase(false);
    // Keep edit mode active when returning
    setIsEditing(true);
  };

  // *** Drag and Drop Handlers ***
  const handleDragStart = (e, wallet, index) => {
    setDraggedWallet(wallet);
    setDraggedIndex(index);
    
    // Make the dragged item semi-transparent
    if (e.target) {
      e.target.style.opacity = '0.4';
    }
    
    // Set data for drag operation
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    
    setDragOverIndex(index);
    
    // Skip if dragging over itself
    if (draggedIndex === index) return;
    
    // Update the ordered wallets list during drag
    const newOrderedWallets = [...orderedWallets];
    const draggedWalletItem = newOrderedWallets[draggedIndex];
    
    // Remove the dragged item
    newOrderedWallets.splice(draggedIndex, 1);
    // Insert it at the new position
    newOrderedWallets.splice(index, 0, draggedWalletItem);
    
    // Update the dragged index to the new position
    setDraggedIndex(index);
    // Update the ordered wallets
    setOrderedWallets(newOrderedWallets);
  };
  
  const handleDragEnd = (e) => {
    // Reset opacity
    if (e.target) {
      e.target.style.opacity = '1';
    }
    
    // If we have a valid drag operation
    if (draggedIndex !== null && draggedWallet) {
      // Immediately save the reordering to persist changes
      if (onReorderWallets) {
        onReorderWallets(orderedWallets);
      }
    }
    
    // Reset drag state
    setDraggedWallet(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Determine if any of the wallets are derived from the current seed phrase
  const hasWalletsFromSeedPhrase = wallets && wallets.length > 0 && wallets.some(wallet => 
    wallet && wallet.address && isWalletFromSeedPhrase(wallet)
  );

  // Safe function to display wallet address
  const getDisplayAddress = (wallet) => {
    if (!wallet || !wallet.address) return 'Unknown';
    return wallet.address.slice(0, 6) + '...' + wallet.address.slice(-4);
  };

  // Get selected wallet information safely
  const getSelectedWalletInfo = () => {
    if (!selectedWalletToken) return { name: 'Unknown', address: 'Unknown' };
    
    try {
      const wallet = getSecureData(selectedWalletToken, true);
      return {
        name: wallet?.name || 'Unknown',
        address: wallet?.address || 'Unknown'
      };
    } catch (error) {
      console.error('Error accessing wallet info:', error);
      return { name: 'Unknown', address: 'Unknown' };
    }
  };

  // Handler for clicking outside modal to close
  const handleOverlayClick = (e) => {
    if (e.target.className === 'wallet-selector-overlay') {
      onClose();
    }
  };

  return (
    <div className="wallet-selector-overlay" onClick={handleOverlayClick}>
      <div className="wallet-selector-content">
        <button className="close-icon" onClick={onClose}>
          <CloseIcon />
        </button>

        {!isAdding && !isEditing && !isViewingKey && !isViewingSeed && <h2>{t('wallet.selectorModal.title')}</h2>}
        {isAdding && !isViewingKey && !isViewingSeed && <h2>{t('wallet.selectorModal.addNew')}</h2>}
        {isEditing && !isViewingKey && !isViewingSeed && <h2>{t('wallet.selectorModal.edit')}</h2>}
        {isViewingKey && <h2>{t('wallet.selectorModal.viewPrivateKey')}</h2>}
        {isViewingSeed && <h2>{t('wallet.selectorModal.viewSeedPhrase')}</h2>}

        {/* LIST MODE - Display wallets */}
        {!isAdding && !isEditing && !isViewingKey && !isViewingSeed && (
          <>
            {orderedWallets && orderedWallets.length > 0 ? (
              <div className="wallet-list">
                {orderedWallets.map((w, idx) => (
                  <div
                    key={w && w.address ? w.address : `wallet-${idx}`}
                    className={`wallet-item ${currentWallet?.address === w?.address ? 'current' : ''}`}
                    onClick={() => w && w.address ? onSwitchWallet(w) : null}
                  >
                    <div className="wallet-lines">
                      <span className="wallet-name-line">
                        {w?.name || t('wallet.selectorModal.account', { number: idx + 1 })}
                      </span>
                      <span className="wallet-address-line">
                        {w && w.address ? w.address.slice(0, 6) + '...' + w.address.slice(-4) : 'Loading...'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-wallets-label">{t('wallet.selectorModal.noWalletsFound')}</p>
            )}

            <div className="wallet-selector-bottom">
              <button className="add-wallet-btn" onClick={() => setIsAdding(true)}>
                {t('wallet.selectorModal.addWallet')}
              </button>
              <button 
                className="edit-wallets-btn" 
                onClick={() => setIsEditing(true)}
                disabled={!orderedWallets || orderedWallets.length === 0}
              >
                {t('wallet.selectorModal.edit')}
              </button>
            </div>
          </>
        )}

        {/* ADD MODE - Create or import wallet */}
        {isAdding && (
          <div className="add-wallet-view">
            <p>{t('wallet.selectorModal.chooseAddMethod')}</p>
            <div className="add-wallet-buttons">
              <button 
                onClick={handleCreateWallet} 
                className="add-option-btn"
                disabled={isProcessing}
              >
                {t('wallet.selectorModal.createNew')}
              </button>
              <button 
                className="add-option-btn" 
                onClick={() => setIsImporting(true)}
                disabled={isProcessing}
              >
                {t('wallet.selectorModal.import')}
              </button>
            </div>

            {isImporting && (
              <div className="import-section">
                <p className="import-instruction">{t('wallet.selectorModal.enterPrivateKeyOnly')}</p>
                <input
                  type="text"
                  className="import-pk-input"
                  placeholder={t('wallet.selectorModal.enterPrivateKeyPlaceholder')}
                  value={hasEnteredImportData ? "••••••••••••••••••••••••••••••••••••••••••" : ""}
                  onChange={(e) => handleImportPKChange(e.target.value)}
                  disabled={isProcessing}
                />
                {importError && (
                  <div className="import-error">{importError}</div>
                )}
                <div className="import-note">
                  <strong>{t('common:note')}:</strong> {t('wallet.selectorModal.seedPhraseImportNote')}
                </div>
                <button 
                  className="add-option-btn" 
                  onClick={handleImportWalletClick}
                  disabled={isProcessing || !hasEnteredImportData}
                >
                  {isProcessing ? t('wallet.selectorModal.importing') : t('wallet.selectorModal.import')}
                </button>
              </div>
            )}

            <button 
              className="close-button-wallet-selector" 
              onClick={handleCancelAdd}
              disabled={isProcessing}
            >
              {t('wallet.selectorModal.buttons.back')}
            </button>
          </div>
        )}

        {/* EDIT MODE - Rename or remove wallets with drag-and-drop */}
        {isEditing && !isViewingKey && !isViewingSeed && (
          <div className="edit-wallet-view">
            {orderedWallets && orderedWallets.length > 0 ? (
              <div className="edit-wallet-list">
                {orderedWallets.map((w, idx) => (
                  <div 
                    key={w && w.address ? w.address : `wallet-edit-${idx}`} 
                    className={`edit-wallet-item ${dragOverIndex === idx ? 'drag-over' : ''}`}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, w, idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                  >
                    {/* Drag handle */}
                    <span className="drag-handle">
                      <DragHandleIcon />
                    </span>
                    
                    {/* Editable name input with auto-save */}
                    <input
                      type="text"
                      className="wallet-name-input"
                      placeholder={t('wallet.selectorModal.account', { number: idx + 1 })}
                      defaultValue={w?.name || ''}
                      onBlur={(e) => w && handleNameChange(w, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur(); // Save on Enter key
                        }
                      }}
                    />

                    {/* Action buttons */}
                    <div className="edit-buttons">
                      {/* View Private Key button */}
                      <button
                        className="view-key-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          w && handleViewPrivateKey(w);
                        }}
                        title={t('wallet.selectorModal.viewPrivateKey')}
                        disabled={!w || !w.address}
                      >
                        <EyeIcon />
                      </button>
                      
                      {/* Remove button */}
                      <button
                        className="remove-x-btn"
                        onClick={() => w && handleRemoveWalletConfirm(w)}
                        title={t('wallet.removeToken')}
                        disabled={!w || !w.address}
                      >
                        <CloseIcon />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-wallets-label">{t('wallet.selectorModal.noWalletsToEdit')}</p>
            )}

            <button className="close-button-wallet-selector" onClick={handleCancelEdit}>
              {t('wallet.selectorModal.buttons.done')}
            </button>
          </div>
        )}

        {/* PRIVATE KEY VIEW MODE - completely separate from edit mode */}
        {isViewingKey && selectedWalletToken && (
          <div className="view-key-container">
            <div className="key-warning">
              <h3>{t('wallet.selectorModal.securityWarning')}</h3>
              <p dangerouslySetInnerHTML={{ __html: t('wallet.selectorModal.privateKeyWarning') }}></p>
            </div>

            <div className="selected-wallet-info">
              <div className="wallet-info-item">
                <span className="wallet-info-label">{t('wallet.selectorModal.walletLabel')}</span>
                <span className="wallet-info-value">{getSelectedWalletInfo().name || t('wallet.selectorModal.unnamed')}</span>
              </div>
              <div className="wallet-info-item">
                <span className="wallet-info-label">{t('wallet.selectorModal.addressLabel')}</span>
                <span className="wallet-info-value">{getSelectedWalletInfo().address || 'Unknown'}</span>
              </div>
            </div>

            {/* Only show password input if user has a password set */}
            {hasPassword && !showPrivateKey && (
              <div className="password-verification">
                <p>{t('wallet.selectorModal.enterPasswordPrompt')}</p>
                <div className="password-input-container">
                  <input
                    type="password"
                    placeholder={t('wallet.selectorModal.enterPasswordPlaceholder')}
                    value={privateKeyPassword}
                    onChange={(e) => setPrivateKeyPassword(e.target.value)}
                    className={privateKeyError ? "pk-password-input error" : "pk-password-input"}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleVerifyPasswordForKey();
                    }}
                  />
                  <button 
                    className="verify-password-btn"
                    onClick={handleVerifyPasswordForKey}
                    disabled={isProcessing}
                  >
                    {isProcessing ? t('wallet.selectorModal.verifying') : t('wallet.selectorModal.verify')}
                  </button>
                </div>
                {privateKeyError && <div className="password-error">{privateKeyError}</div>}
              </div>
            )}

            {showPrivateKey && selectedWalletToken && (
              <div className="private-key-display">
                <div className="private-key-header">
                  <h3>{t('wallet.selectorModal.privateKey')}</h3>
                  <button 
                    className={`copy-key-btn ${copiedKey ? 'copied' : ''}`}
                    onClick={handleCopyPrivateKey}
                  >
                    {copiedKey ? <CheckIcon /> : <CopyIcon />}
                    {copiedKey ? t('wallet.selectorModal.buttons.copied') : t('wallet.selectorModal.buttons.copy')}
                  </button>
                </div>
                <div className="private-key-value">
                  {/* Access the private key securely only when needed for display */}
                  {(() => {
                    try {
                      const wallet = getSecureData(selectedWalletToken, true);
                      return wallet?.privateKey || 'Error accessing private key';
                    } catch (error) {
                      console.error('Error accessing private key for display:', error);
                      return 'Error accessing private key';
                    }
                  })()}
                </div>
              </div>
            )}

            <button className="close-button-wallet-selector" onClick={handleCancelViewKey}>
              {t('wallet.selectorModal.buttons.back')}
            </button>
          </div>
        )}

        {/* SEED PHRASE VIEW MODE */}
        {isViewingSeed && (
          <div className="view-key-container">
            <div className="key-warning">
              <h3>{t('wallet.selectorModal.securityWarning')}</h3>
              <p dangerouslySetInnerHTML={{ __html: t('wallet.selectorModal.seedPhraseWarning') }}></p>
            </div>

            {/* Only show password input if user has a password set */}
            {hasPassword && !showSeedPhrase && (
              <div className="password-verification">
                <p>{t('wallet.selectorModal.enterPasswordPrompt')}</p>
                <div className="password-input-container">
                  <input
                    type="password"
                    placeholder={t('wallet.selectorModal.enterPasswordPlaceholder')}
                    value={privateKeyPassword}
                    onChange={(e) => setPrivateKeyPassword(e.target.value)}
                    className={privateKeyError ? "pk-password-input error" : "pk-password-input"}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleVerifyPasswordForKey();
                    }}
                  />
                  <button 
                    className="verify-password-btn"
                    onClick={handleVerifyPasswordForKey}
                    disabled={isProcessing}
                  >
                    {isProcessing ? t('wallet.selectorModal.verifying') : t('wallet.selectorModal.verify')}
                  </button>
                </div>
                {privateKeyError && <div className="password-error">{privateKeyError}</div>}
              </div>
            )}

            {showSeedPhrase && (
              <div className="seed-phrase-display">
                <div className="private-key-header">
                  <h3>{t('wallet.selectorModal.seedPhrase')}</h3>
                  <button 
                    className={`copy-key-btn ${copiedSeed ? 'copied' : ''}`}
                    onClick={handleCopySeedPhrase}
                  >
                    {copiedSeed ? <CheckIcon /> : <CopyIcon />}
                    {copiedSeed ? t('wallet.selectorModal.buttons.copied') : t('wallet.selectorModal.buttons.copy')}
                  </button>
                </div>
                <div className="seed-phrase-value">
                  {/* Access the seed phrase securely only when needed for display */}
                  {(() => {
                    try {
                      const seedPhrase = getSeedPhrase();
                      if (!seedPhrase) {
                        return t('wallet.selectorModal.noSeedPhrase');
                      }
                      
                      // Display seed phrase in a grid
                      return (
                        <div className="seed-words-grid">
                          {seedPhrase.split(' ').map((word, index) => (
                            <div key={index} className="seed-word">
                              <span className="seed-word-number">{index + 1}.</span> {word}
                            </div>
                          ))}
                        </div>
                      );
                    } catch (error) {
                      console.error('Error accessing seed phrase for display:', error);
                      return 'Error accessing seed phrase';
                    }
                  })()}
                </div>
              </div>
            )}

            <button className="close-button-wallet-selector" onClick={handleCancelViewSeed}>
              {t('wallet.selectorModal.buttons.back')}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default WalletSelectorModal;